import React, { useState } from 'react';
import { Table, Card, Row, Col } from 'antd';
import { ButtonGroup, Button } from '@mui/material';
import moment from 'moment';

function computeReturns(seriesData, interval) {
  const intervalReturns = seriesData.map(series => {
    const intervalData = {};
    series.data.forEach(entry => {
      const date = entry.timestamp; // String date 'YYYY-MM-DD'
      let key;

      // Adjust key based on interval
      if (interval === 'yearly') {
        key = date.split('-')[0]; // Extracts the year
      } else if (interval === 'monthly') {
        key = date.slice(0, 7); // Extracts 'YYYY-MM'
      } else if (interval === 'weekly') {
        key = moment(date).format('YYYY-[W]WW');  // ISO week format from string date
      } else {
        key = date;  // Daily, just use the original date string
      }

      if (!intervalData[key]) {
        intervalData[key] = {
          startValue: entry.close,
          endValue: entry.close,
          startDate: entry.timestamp,
          endDate: entry.timestamp
        };
      } else {
        intervalData[key].endValue = entry.close;
        intervalData[key].endDate = entry.timestamp; // Update the end date for this interval
      }
    });

    const returns = Object.keys(intervalData).map(key => ({
      key,
      return: ((intervalData[key].endValue - intervalData[key].startValue) / intervalData[key].startValue) * 100,
      startDate: intervalData[key].startDate,
      endDate: intervalData[key].endDate
    }));

    return {
      name: series.name,
      data: returns
    };
  });

  // Calculate differences between the first and second series
  const differences = intervalReturns[0].data.map((data, index) => {
    return {
      key: data.key,
      difference: data.return - intervalReturns[1].data[index]?.return || 0
    };
  });

  return { returns: intervalReturns, differences };
}

const YearlyReturnsTable = ({ seriesData }) => {
  const [interval, setInterval] = useState('yearly');
  const [data, setData] = useState(computeReturns(seriesData, interval));
  const { returns, differences } = data;

  const handleIntervalChange = (newInterval) => {
    setInterval(newInterval);
    setData(computeReturns(seriesData, newInterval));
  };

  // Columns for the Ant Design Table
  const columns = [
    {
      title: interval.charAt(0).toUpperCase() + interval.slice(1), // Capitalize interval for column title
      dataIndex: 'key',
      key: 'key',
      align: 'center',
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
      align: 'center',
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
      align: 'center',
    },
    ...returns.map(series => ({
      title: series.name,
      dataIndex: series.name,
      key: series.name,
      align: 'center',
      render: (text) => (
        <span
          style={{
            color: text >= 0 ? 'green' : 'red',
          }}
        >
          {text.toFixed(2)}%
        </span>
      ),
    })),
    {
      title: 'Delta +/-',
      dataIndex: 'difference',
      key: 'difference',
      align: 'center',
      render: (text) => (
        <span
          style={{
            color: text >= 0 ? 'green' : 'red',
          }}
        >
          {text > 0 ? '+' : ''} {text.toFixed(2)}%
        </span>
      ),
    },
  ];

  // Data for the table
  const tableData = returns[0].data.map((_, index) => {
    const row = {
      key: returns[0].data[index].key,
      startDate: returns[0].data[index].startDate,
      endDate: returns[0].data[index].endDate
    };
    returns.forEach(series => {
      row[series.name] = series.data[index].return;
    });
    row.difference = differences[index].difference;
    return row;
  });
  return (
    <Card bordered={false} style={{ width: '100%', marginBottom: '20px' }}>
      <ButtonGroup variant="outlined" color="primary" style={{ marginBottom: '16px' }}>
        <Button onClick={() => handleIntervalChange('weekly')} variant={interval === 'weekly' ? 'contained' : 'outlined'}>
          Weekly
        </Button>
        <Button onClick={() => handleIntervalChange('monthly')} variant={interval === 'monthly' ? 'contained' : 'outlined'}>
          Monthly
        </Button>
        <Button onClick={() => handleIntervalChange('yearly')} variant={interval === 'yearly' ? 'contained' : 'outlined'}>
          Yearly
        </Button>
      </ButtonGroup>
      <Table
        dataSource={tableData}
        columns={columns}
        pagination={false}
        bordered
        size="large"
        rowKey="key"
        scroll={{ y: 600 }}
        style={{
          maxWidth: 800
        }}
      />
    </Card>
  );
};

export default YearlyReturnsTable;
